import React from 'react';

// A set of selected ids over a page of selectable things. The vault keeps this inside a Context
// with its own filter rules; the queue and the proposals table only need the set, the page
// select-all state and a clear, so it is a hook.
//
// `selectableIds` is the page's selectable ids in order. An id that stops being selectable (the
// row left the page after a decide, or a filter narrowed the list) is dropped on the next render so
// a bulk action never carries a stale id.
export const useSelection = (selectableIds = []) => {
  const [selected, setSelected] = React.useState(() => new Set());

  const selectableKey = selectableIds.join('\u0000');
  const selectableSet = React.useMemo(() => new Set(selectableIds), [selectableKey]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    setSelected((prev) => {
      let changed = false;
      const next = new Set();
      prev.forEach((id) => {
        if (selectableSet.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [selectableSet]);

  const isSelected = React.useCallback((id) => selected.has(id), [selected]);

  const toggle = React.useCallback(
    (id) => {
      if (!selectableSet.has(id)) return;
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    },
    [selectableSet]
  );

  const setPage = React.useCallback(
    (shouldSelect) => {
      setSelected((prev) => {
        const next = new Set(prev);
        selectableIds.forEach((id) => {
          if (shouldSelect) next.add(id);
          else next.delete(id);
        });
        return next;
      });
    },
    [selectableKey] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const clear = React.useCallback(() => setSelected(new Set()), []);

  const selectedOnPage = selectableIds.filter((id) => selected.has(id)).length;
  const isPageFullySelected = selectableIds.length > 0 && selectedOnPage === selectableIds.length;
  const isPagePartiallySelected = selectedOnPage > 0 && !isPageFullySelected;

  return {
    selectedIds: Array.from(selected),
    count: selected.size,
    isSelected,
    toggle,
    setPage,
    clear,
    isPageFullySelected,
    isPagePartiallySelected,
  };
};
